import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getUserManager, getCachedSubForSid } from './oidcManager';
import { initTrustShieldSSO } from './initTrustShieldSSO';

export function useTrustShieldAuth(sid: string, enabled: boolean) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!sid) return;
        let cancelled = false;
        let cleanup: (() => void) | null = null;
        (async () => {
            const sub = await initTrustShieldSSO(sid);
            if (cancelled || !sub) return;
            const mgr = getUserManager(sub);
            const onUserLoaded = () => {
                queryClient.invalidateQueries({ queryKey: ['trustShieldJwt'] });
            };
            mgr.events.addUserLoaded(onUserLoaded);
            cleanup = () => mgr.events.removeUserLoaded(onUserLoaded);
        })();
        return () => {
            cancelled = true;
            cleanup?.();
        };
    }, [queryClient, sid]);

    const { data: jwt, isLoading, error } = useQuery<string>({
        queryKey: ['trustShieldJwt', sid],
        queryFn: async () => {
            const cachedSub = getCachedSubForSid(sid);
            if (cachedSub) {
                const user = await getUserManager(cachedSub).getUser();
                if (user && !user.expired) return user.access_token;
            }

            const refreshedSub = await initTrustShieldSSO(sid);
            if (!refreshedSub) throw new Error('No valid OIDC access token.');
            const user = await getUserManager(refreshedSub).getUser();
            if (user && !user.expired) return user.access_token;

            throw new Error('No valid OIDC access token.');
        },
        enabled: enabled && Boolean(sid),
        // No staleTime override. The queryFn has its own cheap fast-path
        // (localStorage read + expiry check, no network) when the AT is still
        // fresh, so re-running on every mount is essentially free in the
        // happy case. Setting `staleTime: Infinity` here used to mask the
        // stored AT going expired across remounts: React Query would return
        // the cached (now-stale) jwt without re-running queryFn, downstream
        // queries (matter fetch, etc.) would 401 immediately, and the
        // useEffect-driven /trusted-token refresh below couldn't propagate
        // its result back into the cache because writeStoredUser doesn't
        // fire oidc-client-ts's UserLoaded event.
        //
        // Override the QueryClient's global `refetchOnWindowFocus: false`
        // for this query specifically. After a long-idle period both AT and
        // RT can expire while the tab is hidden; on return we want the queryFn
        // to re-run and call /trusted-token. The fast-path means most focus
        // events don't hit the network.
        refetchOnWindowFocus: true,
    });

    return { jwt: jwt ?? null, isLoading, error };
}
